import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Building2, 
  Plus, 
  Trash2, 
  ExternalLink,
  Search,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Save,
  Eye,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Printer
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { api, formatCurrency, STATES, PROPERTY_TYPES } from "@/lib/api";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const emptyComparable = {
  address: "",
  land_size_sqm: "",
  dwelling_size_sqm: "",
  bedrooms: "",
  bathrooms: "",
  car_spaces: "",
  has_pool: false,
  listing_url: "",
  sale_price: ""
};

const emptyAnalysis = {
  address: "",
  suburb: "",
  state: "",
  postcode: "",
  property_type: "",
  purchase_price: "",
  purchase_date: "",
  loan_amount: "",
  interest_rate: "6.0",
  loan_term_years: "30",
  rental_income_weekly: "",
  is_owner_occupied: false,
  land_value: "",
  notes: "",
  land_size_sqm: "",
  dwelling_size_sqm: "",
  bedrooms: "",
  bathrooms: "",
  car_spaces: "",
  has_pool: false,
  listing_url: "",
  comparables: [
    { ...emptyComparable },
    { ...emptyComparable },
    { ...emptyComparable },
    { ...emptyComparable },
    { ...emptyComparable }
  ],
  // Deal-breaker checklist
  checked_flood_overlay: false,
  checked_bushfire_overlay: false,
  checked_road_noise: false,
  checked_train_noise: false,
  checked_sewer_pipes: false
};

const dealBreakerItems = [
  { key: "checked_flood_overlay", label: "Flood Overlay", description: "Checked council flood maps and property is clear of flood zones" },
  { key: "checked_bushfire_overlay", label: "Bushfire Overlay", description: "Checked bushfire prone land maps and assessed risk level" },
  { key: "checked_road_noise", label: "Road Noise", description: "Visited property at different times to assess traffic noise impact" },
  { key: "checked_train_noise", label: "Train Noise", description: "Checked proximity to train lines and assessed noise impact" },
  { key: "checked_sewer_pipes", label: "Sewer Pipe Locations", description: "Obtained dial-before-you-dig report and checked easements" }
];

export default function PropertyAnalyser() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showSavedList, setShowSavedList] = useState(false);
  const [formData, setFormData] = useState({ ...emptyAnalysis });
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showForecastDialog, setShowForecastDialog] = useState(false);
  const [forecastYear, setForecastYear] = useState(1);

  // Load saved analyses
  useEffect(() => {
    const fetchAnalyses = async () => {
      try {
        const response = await api.get("/property-analyser");
        setSavedAnalyses(response.data.analyses || []);
      } catch (error) {
        console.error("Error fetching analyses:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAnalyses();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear analysis result when form changes
    setAnalysisResult(null);
  };

  // Handle address autocomplete selection for subject property
  const handleSubjectAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.streetAddress,
      suburb: addressData.suburb,
      state: addressData.state,
      postcode: addressData.postcode,
    }));
    setAnalysisResult(null);
  };

  // Handle address autocomplete selection for comparables
  const handleComparableAddressSelect = (index, addressData) => {
    setFormData(prev => {
      const newComparables = [...prev.comparables];
      newComparables[index] = {
        ...newComparables[index],
        address: `${addressData.streetAddress}, ${addressData.suburb}`,
      };
      return { ...prev, comparables: newComparables };
    });
    setAnalysisResult(null);
  };

  const handleComparableChange = (index, field, value) => {
    setFormData(prev => {
      const newComparables = [...prev.comparables];
      newComparables[index] = { ...newComparables[index], [field]: value };
      return { ...prev, comparables: newComparables };
    });
    setAnalysisResult(null);
  };

  const validateForm = () => {
    const required = ["address", "suburb", "state", "postcode", "property_type", "purchase_price"];
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in the ${field.replace(/_/g, " ")} field`);
        return false;
      }
    }
    return true;
  };

  const handleAnalyse = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Format the data for API
      const payload = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        loan_amount: parseFloat(formData.loan_amount) || 0,
        interest_rate: parseFloat(formData.interest_rate) || 6.0,
        loan_term_years: parseInt(formData.loan_term_years) || 30,
        rental_income_weekly: parseFloat(formData.rental_income_weekly) || 0,
        land_value: parseFloat(formData.land_value) || 0,
        land_size_sqm: parseFloat(formData.land_size_sqm) || null,
        dwelling_size_sqm: parseFloat(formData.dwelling_size_sqm) || null,
        bedrooms: parseInt(formData.bedrooms) || null,
        bathrooms: parseInt(formData.bathrooms) || null,
        car_spaces: parseInt(formData.car_spaces) || null,
        comparables: formData.comparables
          .filter(c => c.sale_price && c.address) // Only include comparables with price and address
          .map(c => ({
            ...c,
            sale_price: parseFloat(c.sale_price) || 0,
            land_size_sqm: parseFloat(c.land_size_sqm) || null,
            dwelling_size_sqm: parseFloat(c.dwelling_size_sqm) || null,
            bedrooms: parseInt(c.bedrooms) || null,
            bathrooms: parseInt(c.bathrooms) || null,
            car_spaces: parseInt(c.car_spaces) || null,
          }))
      };

      let response;
      if (selectedAnalysis) {
        // Update existing
        response = await api.put(`/property-analyser/${selectedAnalysis.id}`, payload);
        toast.success("Analysis updated successfully!");
      } else {
        // Create new
        response = await api.post("/property-analyser", payload);
        toast.success("Analysis saved successfully!");
      }

      setAnalysisResult(response.data);
      setSelectedAnalysis(response.data);
      
      // Refresh saved analyses list
      const listResponse = await api.get("/property-analyser");
      setSavedAnalyses(listResponse.data.analyses || []);
    } catch (error) {
      console.error("Error saving analysis:", error);
      toast.error("Failed to save analysis. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadAnalysis = (analysis) => {
    setFormData({
      ...analysis,
      purchase_price: analysis.purchase_price?.toString() || "",
      loan_amount: analysis.loan_amount?.toString() || "",
      interest_rate: analysis.interest_rate?.toString() || "6.0",
      loan_term_years: analysis.loan_term_years?.toString() || "30",
      rental_income_weekly: analysis.rental_income_weekly?.toString() || "",
      land_value: analysis.land_value?.toString() || "",
      land_size_sqm: analysis.land_size_sqm?.toString() || "",
      dwelling_size_sqm: analysis.dwelling_size_sqm?.toString() || "",
      bedrooms: analysis.bedrooms?.toString() || "",
      bathrooms: analysis.bathrooms?.toString() || "",
      car_spaces: analysis.car_spaces?.toString() || "",
      comparables: analysis.comparables?.length > 0 
        ? analysis.comparables.map(c => ({
            ...c,
            sale_price: c.sale_price?.toString() || "",
            land_size_sqm: c.land_size_sqm?.toString() || "",
            dwelling_size_sqm: c.dwelling_size_sqm?.toString() || "",
            bedrooms: c.bedrooms?.toString() || "",
            bathrooms: c.bathrooms?.toString() || "",
            car_spaces: c.car_spaces?.toString() || "",
          })).concat(Array(5 - analysis.comparables.length).fill({ ...emptyComparable })).slice(0, 5)
        : Array(5).fill({ ...emptyComparable }),
      // Load checklist items
      checked_flood_overlay: analysis.checked_flood_overlay || false,
      checked_bushfire_overlay: analysis.checked_bushfire_overlay || false,
      checked_road_noise: analysis.checked_road_noise || false,
      checked_train_noise: analysis.checked_train_noise || false,
      checked_sewer_pipes: analysis.checked_sewer_pipes || false
    });
    setSelectedAnalysis(analysis);
    setAnalysisResult(analysis);
    setShowSavedList(false);
  };

  const handleDelete = async (analysisId) => {
    if (!confirm("Are you sure you want to delete this analysis?")) return;
    
    try {
      await api.delete(`/property-analyser/${analysisId}`);
      toast.success("Analysis deleted");
      
      // Refresh list
      const response = await api.get("/property-analyser");
      setSavedAnalyses(response.data.analyses || []);
      
      // Clear form if this was the selected analysis
      if (selectedAnalysis?.id === analysisId) {
        setFormData({ ...emptyAnalysis });
        setSelectedAnalysis(null);
        setAnalysisResult(null);
      }
    } catch (error) {
      toast.error("Failed to delete analysis");
    }
  };

  const handleNewAnalysis = () => {
    setFormData({ ...emptyAnalysis });
    setSelectedAnalysis(null);
    setAnalysisResult(null);
    setShowSavedList(false);
  };

  const handleAddToForecast = () => {
    // Can work with either saved analysis or form data
    setShowForecastDialog(true);
  };

  const confirmAddToForecast = () => {
    // Use saved analysis data if available, otherwise use form data
    const dataSource = analysisResult || formData;
    
    const eventData = {
      year: forecastYear,
      event_type: "buy",
      property_value: parseFloat(dataSource.purchase_price) || 0,
      loan_amount: parseFloat(dataSource.loan_amount) || 0,
      weekly_rent: parseFloat(dataSource.rental_income_weekly) || 0,
      interest_rate: parseFloat(dataSource.interest_rate) || 6.0,
      loan_term_years: parseInt(dataSource.loan_term_years) || 30,
      from_analysis: true,
      analysis_address: dataSource.address
    };
    
    // Store in sessionStorage and navigate
    sessionStorage.setItem('forecastEvent', JSON.stringify(eventData));
    navigate('/forecasting');
    toast.success("Redirecting to Forecasting with property data...");
  };

  const handlePrintPDF = () => {
    // Expand all accordion sections before printing
    const accordionTriggers = document.querySelectorAll('[data-radix-accordion-trigger][data-state="closed"]');
    accordionTriggers.forEach(trigger => trigger.click());
    
    // Small delay to allow accordions to expand, then print
    setTimeout(() => {
      window.print();
    }, 300);
  };

  const filledComparablesCount = formData.comparables.filter(c => c.sale_price && c.address).length;

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in" data-testid="property-analyser-page">
      {/* Print Header - Only visible when printing */}
      <div className="print-header hidden print:block">
        <h1>Property Analysis Report</h1>
        {formData.address && (
          <p className="text-lg font-semibold">{formData.address}</p>
        )}
        {formData.suburb && formData.state && (
          <p>{formData.suburb}, {formData.state} {formData.postcode}</p>
        )}
        <p className="print-date">Generated: {new Date().toLocaleDateString('en-AU', { 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric' 
        })}</p>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              Property Analyser
            </h2>
            <p className="text-slate-500">
              Assess potential purchases by comparing against comparable sales
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowSavedList(!showSavedList)}
            className="gap-2"
            data-testid="view-saved-btn"
          >
            <FileText className="w-4 h-4" />
            Saved ({savedAnalyses.length})
          </Button>
          <Button 
            variant="outline" 
            onClick={handleNewAnalysis}
            className="gap-2"
            data-testid="new-analysis-btn"
          >
            <Plus className="w-4 h-4" />
            New Analysis
          </Button>
        </div>
      </div>

      {/* Saved Analyses List */}
      {showSavedList && (
        <Card className="print:hidden">
          <CardHeader>
            <CardTitle className="text-lg">Saved Analyses</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : savedAnalyses.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No saved analyses yet</p>
            ) : (
              <div className="space-y-2">
                {savedAnalyses.map((analysis) => (
                  <div 
                    key={analysis.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{analysis.address}</p>
                      <p className="text-sm text-slate-500">
                        {analysis.suburb}, {analysis.state} • {formatCurrency(analysis.purchase_price)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => loadAnalysis(analysis)}
                        data-testid={`load-analysis-${analysis.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(analysis.id)}
                        data-testid={`delete-analysis-${analysis.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Analysis Result Banner - Shows price comparison without color coding */}
      {analysisResult && analysisResult.average_comparable_price && (
        <Card className="border-2 border-slate-200 bg-slate-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">
                  Price Comparison Summary
                </h3>
                <p className="text-slate-600">
                  Subject Price: <strong>{formatCurrency(analysisResult.purchase_price)}</strong>
                  {" | "}
                  Average Comparable: <strong>{formatCurrency(analysisResult.average_comparable_price)}</strong>
                  {analysisResult.price_variance_percent !== null && (
                    <>
                      {" | "}
                      Variance: <strong>{analysisResult.price_variance_percent > 0 ? '+' : ''}{analysisResult.price_variance_percent?.toFixed(1)}%</strong>
                    </>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subject Property Form */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#547d91]" />
            Subject Property
          </CardTitle>
          <CardDescription>
            Enter details of the property you're considering purchasing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Address Details */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address">Street Address *</Label>
              <AddressAutocomplete
                id="address"
                placeholder="Start typing an address..."
                value={formData.address}
                onChange={(value) => handleChange("address", value)}
                onAddressSelect={handleSubjectAddressSelect}
                data-testid="subject-address-input"
              />
              <p className="text-xs text-slate-500">Start typing to auto-populate address fields</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="suburb">Suburb *</Label>
                <Input
                  id="suburb"
                  placeholder="Sydney"
                  value={formData.suburb}
                  onChange={(e) => handleChange("suburb", e.target.value)}
                  data-testid="subject-suburb-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Select 
                  value={formData.state} 
                  onValueChange={(value) => handleChange("state", value)}
                >
                  <SelectTrigger data-testid="subject-state-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode *</Label>
                <Input
                  id="postcode"
                  placeholder="2000"
                  value={formData.postcode}
                  onChange={(e) => handleChange("postcode", e.target.value)}
                  data-testid="subject-postcode-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_type">Type *</Label>
                <Select 
                  value={formData.property_type} 
                  onValueChange={(value) => handleChange("property_type", value)}
                >
                  <SelectTrigger data-testid="subject-type-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Financial Details */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-4">Financial Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="purchase_price">Asking/Expected Price *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    id="purchase_price"
                    type="number"
                    className="pl-7"
                    placeholder="750000"
                    value={formData.purchase_price}
                    onChange={(e) => handleChange("purchase_price", e.target.value)}
                    data-testid="subject-price-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="loan_amount">Loan Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    id="loan_amount"
                    type="number"
                    className="pl-7"
                    placeholder="600000"
                    value={formData.loan_amount}
                    onChange={(e) => handleChange("loan_amount", e.target.value)}
                    data-testid="subject-loan-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rental_income_weekly">Weekly Rent (Expected)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    id="rental_income_weekly"
                    type="number"
                    className="pl-7"
                    placeholder="650"
                    value={formData.rental_income_weekly}
                    onChange={(e) => handleChange("rental_income_weekly", e.target.value)}
                    data-testid="subject-rent-input"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="interest_rate">Interest Rate (%)</Label>
                <Input
                  id="interest_rate"
                  type="number"
                  step="0.01"
                  placeholder="6.0"
                  value={formData.interest_rate}
                  onChange={(e) => handleChange("interest_rate", e.target.value)}
                  data-testid="subject-interest-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="loan_term_years">Loan Term (years)</Label>
                <Input
                  id="loan_term_years"
                  type="number"
                  placeholder="30"
                  value={formData.loan_term_years}
                  onChange={(e) => handleChange("loan_term_years", e.target.value)}
                  data-testid="subject-term-input"
                />
              </div>
            </div>
          </div>

          {/* Property Features */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-4">Property Features (for comparison)</h4>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="space-y-2">
                <Label htmlFor="land_size_sqm">Land (sqm)</Label>
                <Input
                  id="land_size_sqm"
                  type="number"
                  placeholder="600"
                  value={formData.land_size_sqm}
                  onChange={(e) => handleChange("land_size_sqm", e.target.value)}
                  data-testid="subject-land-size-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dwelling_size_sqm">Dwelling (sqm)</Label>
                <Input
                  id="dwelling_size_sqm"
                  type="number"
                  placeholder="200"
                  value={formData.dwelling_size_sqm}
                  onChange={(e) => handleChange("dwelling_size_sqm", e.target.value)}
                  data-testid="subject-dwelling-size-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bedrooms">Beds</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  placeholder="4"
                  value={formData.bedrooms}
                  onChange={(e) => handleChange("bedrooms", e.target.value)}
                  data-testid="subject-beds-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bathrooms">Baths</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  placeholder="2"
                  value={formData.bathrooms}
                  onChange={(e) => handleChange("bathrooms", e.target.value)}
                  data-testid="subject-baths-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="car_spaces">Cars</Label>
                <Input
                  id="car_spaces"
                  type="number"
                  placeholder="2"
                  value={formData.car_spaces}
                  onChange={(e) => handleChange("car_spaces", e.target.value)}
                  data-testid="subject-cars-input"
                />
              </div>
              <div className="space-y-2 flex flex-col justify-end">
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    id="has_pool"
                    checked={formData.has_pool}
                    onCheckedChange={(checked) => handleChange("has_pool", checked)}
                    data-testid="subject-pool-switch"
                  />
                  <Label htmlFor="has_pool">Pool</Label>
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <Label htmlFor="listing_url">Listing URL</Label>
              <Input
                id="listing_url"
                type="url"
                placeholder="https://www.realestate.com.au/..."
                value={formData.listing_url}
                onChange={(e) => handleChange("listing_url", e.target.value)}
                data-testid="subject-listing-url-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comparable Sales */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Search className="w-5 h-5 text-[#547d91]" />
            Comparable Sales Evidence ({filledComparablesCount}/5)
          </CardTitle>
          <CardDescription>
            Enter up to 5 recent comparable sales to compare against the subject property
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="space-y-2">
            {formData.comparables.map((comp, index) => (
              <AccordionItem 
                key={index} 
                value={`comparable-${index}`}
                className="border rounded-lg px-4"
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">Comparable {index + 1}</span>
                    {comp.address && comp.sale_price && (
                      <Badge variant="secondary" className="bg-slate-100">
                        {comp.address.substring(0, 30)}{comp.address.length > 30 ? '...' : ''} - {formatCurrency(parseFloat(comp.sale_price))}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Address *</Label>
                      <AddressAutocomplete
                        placeholder="Start typing address..."
                        value={comp.address}
                        onChange={(value) => handleComparableChange(index, "address", value)}
                        onAddressSelect={(addressData) => handleComparableAddressSelect(index, addressData)}
                        data-testid={`comp-${index}-address-input`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sale Price *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <Input
                          type="number"
                          className="pl-7"
                          placeholder="720000"
                          value={comp.sale_price}
                          onChange={(e) => handleComparableChange(index, "sale_price", e.target.value)}
                          data-testid={`comp-${index}-price-input`}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <div className="space-y-2">
                      <Label>Land (sqm)</Label>
                      <Input
                        type="number"
                        placeholder="600"
                        value={comp.land_size_sqm}
                        onChange={(e) => handleComparableChange(index, "land_size_sqm", e.target.value)}
                        data-testid={`comp-${index}-land-input`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Dwelling (sqm)</Label>
                      <Input
                        type="number"
                        placeholder="200"
                        value={comp.dwelling_size_sqm}
                        onChange={(e) => handleComparableChange(index, "dwelling_size_sqm", e.target.value)}
                        data-testid={`comp-${index}-dwelling-input`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Beds</Label>
                      <Input
                        type="number"
                        placeholder="4"
                        value={comp.bedrooms}
                        onChange={(e) => handleComparableChange(index, "bedrooms", e.target.value)}
                        data-testid={`comp-${index}-beds-input`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Baths</Label>
                      <Input
                        type="number"
                        placeholder="2"
                        value={comp.bathrooms}
                        onChange={(e) => handleComparableChange(index, "bathrooms", e.target.value)}
                        data-testid={`comp-${index}-baths-input`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cars</Label>
                      <Input
                        type="number"
                        placeholder="2"
                        value={comp.car_spaces}
                        onChange={(e) => handleComparableChange(index, "car_spaces", e.target.value)}
                        data-testid={`comp-${index}-cars-input`}
                      />
                    </div>
                    <div className="space-y-2 flex flex-col justify-end">
                      <div className="flex items-center gap-2 h-10">
                        <Switch
                          checked={comp.has_pool}
                          onCheckedChange={(checked) => handleComparableChange(index, "has_pool", checked)}
                          data-testid={`comp-${index}-pool-switch`}
                        />
                        <Label>Pool</Label>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Listing URL</Label>
                    <div className="flex gap-2">
                      <Input
                        type="url"
                        placeholder="https://www.realestate.com.au/..."
                        value={comp.listing_url}
                        onChange={(e) => handleComparableChange(index, "listing_url", e.target.value)}
                        data-testid={`comp-${index}-url-input`}
                      />
                      {comp.listing_url && (
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => window.open(comp.listing_url, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Deal-Breaker Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Deal-Breaker Checklist
            <Badge 
              variant="secondary" 
              className={`ml-2 ${
                dealBreakerItems.every(item => formData[item.key]) 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-amber-100 text-amber-700'
              }`}
            >
              {dealBreakerItems.filter(item => formData[item.key]).length}/{dealBreakerItems.length} Complete
            </Badge>
          </CardTitle>
          <CardDescription>
            Confirm you have checked these critical due diligence items before proceeding
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {dealBreakerItems.map((item) => (
              <div 
                key={item.key}
                className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                  formData[item.key] 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                }`}
              >
                <Checkbox
                  id={item.key}
                  checked={formData[item.key]}
                  onCheckedChange={(checked) => handleChange(item.key, checked)}
                  className="mt-1"
                  data-testid={`checklist-${item.key}`}
                />
                <div className="flex-1">
                  <label 
                    htmlFor={item.key} 
                    className="font-medium cursor-pointer flex items-center gap-2"
                  >
                    {formData[item.key] ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Circle className="w-4 h-4 text-slate-400" />
                    )}
                    {item.label}
                  </label>
                  <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          {!dealBreakerItems.every(item => formData[item.key]) && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                <strong>Warning:</strong> Not all due diligence items have been checked. 
                Ensure you've investigated all potential deal-breakers before purchasing.
              </p>
            </div>
          )}
          
          {dealBreakerItems.every(item => formData[item.key]) && (
            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-700">
                <strong>All clear!</strong> All due diligence items have been checked.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between print:hidden">
        <div className="text-sm text-slate-500">
          {filledComparablesCount === 0 && "Add at least one comparable for price analysis"}
        </div>
        <div className="flex items-center gap-4">
          {/* Show Print/PDF and Add to Forecast when form has data */}
          {formData.address && formData.purchase_price && (
            <>
              <Button 
                variant="outline"
                onClick={handlePrintPDF}
                className="gap-2"
                data-testid="print-pdf-btn"
              >
                <Printer className="w-4 h-4" />
                Print / PDF
              </Button>
              <Button 
                variant="outline"
                onClick={handleAddToForecast}
                className="gap-2"
                data-testid="add-to-forecast-btn"
              >
                <TrendingUp className="w-4 h-4" />
                Add to Forecast
              </Button>
            </>
          )}
          <Button 
            onClick={handleAnalyse}
            className="bg-[#547d91] hover:bg-[#456a7a] gap-2"
            disabled={isSubmitting}
            data-testid="analyse-btn"
          >
            <Save className="w-4 h-4" />
            {isSubmitting ? "Saving..." : selectedAnalysis ? "Update Analysis" : "Save & Analyse"}
          </Button>
        </div>
      </div>

      {/* Add to Forecast Dialog */}
      <Dialog open={showForecastDialog} onOpenChange={setShowForecastDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Forecast</DialogTitle>
            <DialogDescription>
              Add this property analysis to your portfolio forecast as a planned purchase.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium">{formData.address || analysisResult?.address}</p>
              <p className="text-sm text-slate-500">
                {formatCurrency(parseFloat(formData.purchase_price) || analysisResult?.purchase_price || 0)} • 
                {(formData.rental_income_weekly || analysisResult?.rental_income_weekly) ? ` $${formData.rental_income_weekly || analysisResult?.rental_income_weekly}/week rent` : ' No rent'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>In which year do you plan to purchase?</Label>
              <Select 
                value={forecastYear.toString()} 
                onValueChange={(v) => setForecastYear(parseInt(v))}
              >
                <SelectTrigger data-testid="forecast-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      Year {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForecastDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmAddToForecast}
              className="bg-[#547d91] hover:bg-[#456a7a]"
              data-testid="confirm-forecast-btn"
            >
              Add to Forecast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Footer - Only visible when printing */}
      <div className="print-footer hidden print:block">
        <p>The Portfolio Accelerator - Property Analysis Report | Powered by Investeps Property</p>
      </div>
    </div>
  );
}
